// Normaliser un besoin. Même dispositif que la lecture d'un CV : on colle la demande du
// client, le modèle propose une fiche, un humain la relit. Le même formulaire de relecture
// sert de saisie manuelle — il n'y a aucune raison d'en entretenir deux.
import { api, direRefus } from '../api.ts';
import { $, blocAttente, brut, compteur, h, mot, peindre, valeur, type Brut } from '../ui.ts';
import { type Referentiel } from '../etat.ts';
import { POSTURES_IA } from '../../domaine/ia.ts';
import { REGIONS, TAILLES_CLIENT } from '../../domaine/mission.ts';

interface Livrable { livrable: string; echeance?: string | null }
interface Axe { axe: string; objectif?: string | null; interlocuteurs?: string | null }
interface Responsabilite { titre: string; points: string[] }

interface Fiche {
  intitule: string; client?: string; secteur?: string; taille_client?: string; region?: string;
  role_grille?: string; demarrage?: string; duree_texte?: string; charge_texte?: string;
  budget_min_eur?: number; budget_max_eur?: number; zones: string[];
  enjeu: string; contexte?: string; rattachement?: string; livrables: Livrable[];
  competences_requises: string[]; competences_souhaitables: string[];
  intensite_ia?: string; ia_attendu?: string; criteres_succes: string[];
  axes: Axe[]; responsabilites: Responsabilite[];
  experience: string[]; savoir_etre: string[]; modalites: string[];
  deduits: string[]; questions: string[];
}

const VIDE: Fiche = {
  intitule: '', zones: [], enjeu: '', livrables: [],
  competences_requises: [], competences_souhaitables: [], criteres_succes: [],
  axes: [], responsabilites: [], experience: [], savoir_etre: [], modalites: [],
  deduits: [], questions: [],
};

interface Session {
  ref: Referentiel; fini: (id: string) => void; besoin: string; traitement: string; empreinte: string;
  /** Le document déposé, s'il y en a un : il rejoint la mission une fois celle-ci créée. */
  fichier: File | null;
  /**
   * Ce que le modèle a rendu, gardé entier. Le formulaire ne montre pas tout — les axes
   * et les responsabilités s'y relisent sans s'y éditer — et ce qui n'est pas montré doit
   * repartir tel quel. C'est la même règle que pour la synthèse d'un profil, et elle vient
   * du même défaut : un formulaire qui reconstruit perd ce qu'il n'affiche pas.
   */
  propose: Fiche;
  /**
   * La clé d'idempotence de cette création. Elle naît avec le brief et ne change plus :
   * deux envois qui la portent décrivent la même intention, et le serveur n'en crée qu'une.
   */
  cle: string;
}
let vivante: Session | null = null;
let ecoute = false;

const cadre = (titre: string, corps: Brut, pied: Brut): Brut => h`
  <div class="voile" data-act="fermer-voile"><div class="couche large-c">
    <div class="ct"><div><div class="sur">NOUVELLE MISSION</div><h2>${titre}</h2></div>
      <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
    <div class="cc">${corps}</div>
    <div class="cp">${pied}</div>
  </div></div>`;

const etiquette = (libelle: string, cle: string, deduits: string[]) => h`
  <label class="lab" for="b-${cle}">${libelle}
    ${deduits.includes(cle) ? h`<span class="pt" style="color:var(--copper);letter-spacing:0">· déduit</span>` : ''}</label>`;

const champ = (libelle: string, cle: string, v: unknown, d: string[], type = 'text') => h`
  <div>${etiquette(libelle, cle, d)}
    <input class="champ" id="b-${cle}" type="${type}" value="${v ?? ''}"></div>`;

const choix = (libelle: string, cle: string, options: readonly string[], v: string | undefined, d: string[]) => h`
  <div>${etiquette(libelle, cle, d)}
    <select class="champ" id="b-${cle}"><option value="">—</option>
      ${options.map((o) => h`<option value="${o}" ${o === v ? brut('selected') : ''}>${o}</option>`)}</select></div>`;

const pave = (libelle: string, cle: string, v: string[] | string | undefined, d: string[], rangs = 3, aide = '') => h`
  <div>${etiquette(libelle, cle, d)}
    <textarea class="champ" id="b-${cle}" rows="${rangs}">${Array.isArray(v) ? v.join('\n') : v ?? ''}</textarea>
    ${aide ? h`<p class="pt sourd" style="margin:4px 0 0">${aide}</p>` : ''}</div>`;

export function ouvrirBrief(ref: Referentiel, fini: (id: string) => void): void {
  // La clé d'idempotence naît avec le brief et ne change plus : c'est elle qui dit au
  // serveur que deux envois décrivent la même intention de créer, et non deux missions.
  // Tirée ici plutôt qu'au moment du clic, pour qu'un second clic porte bien la même.
  vivante = { ref, fini, besoin: '', traitement: '', empreinte: '', fichier: null,
              propose: VIDE, cle: crypto.randomUUID() };
  poserEcoute();
  saisie();
}

// ─── 1. La demande du client ────────────────────────────────────────────────

function saisie(): void {
  peindre('#couche', cadre('Le besoin, tel qu’il est arrivé', h`
    <div class="enc gl"><span class="sur">COLLEZ LA DEMANDE TELLE QUELLE</span>
      <p>Le courriel, les notes de la réunion, le message. N'arrangez rien : ce qui est mal dit
         dans la demande doit rester mal dit, c'est ce que le modèle signalera comme question
         à poser au client.</p></div>
    <textarea class="champ" id="b-besoin" rows="9"
      placeholder="Bonjour, nous cherchons quelqu'un pour…">${vivante?.besoin ?? ''}</textarea>

    <div class="filet-l"><span class="sur">OU DÉPOSEZ UN DOCUMENT</span>
      <span class="pt sourd">PDF, Word .docx, .txt ou .md</span></div>
    <div class="depose" id="b-zone">
      <span class="sm sourd">Une fiche de mission déjà rédigée, un cahier des charges, une
        annonce du client. Elle sera lue, et <b>jointe à la mission</b> : c'est elle qui
        partira au candidat plutôt qu'une fiche reconstituée.</span>
      <input class="champ" type="file" id="b-fichier" accept=".pdf,.docx,.txt,.md"></div>

    <p class="err" id="err" hidden></p>`,
    h`<button class="bt" data-act="fermer">Annuler</button>
      <button class="bt" data-b="main">Remplir à la main</button>
      <button class="bt p" data-b="normaliser">Lire et normaliser</button>`));
}

function attente(): () => void {
  peindre('#couche', cadre('Normalisation en cours',
    blocAttente('LE MODÈLE LIT LA DEMANDE',
      "Il en tire un enjeu, des compétences requises et souhaitables, et la liste de ce qu'il faudra "
      + "demander au client. Vous relirez tout avant que la mission n'existe."),
    h`<span class="sm sourd pousse">comptez quelques secondes</span>`));
  return compteur('#chrono');
}

// ─── 2. La relecture, qui sert aussi de saisie ──────────────────────────────

function relecture(f: Fiche, cout: number | null): void {
  const ref = vivante!.ref;
  const d = f.deduits;
  peindre('#couche', cadre(f.intitule || 'Nouvelle mission', h`
    ${f.questions.length ? h`<div class="enc ar"><span class="sur">À DEMANDER AU CLIENT AVANT DE CHERCHER</span>
      <ul style="margin:6px 0 0;padding-left:17px">${f.questions.map((x) =>
        h`<li style="font-size:12.5px;line-height:1.5">${x}</li>`)}</ul></div>` : ''}

    ${champ('INTITULÉ', 'intitule', f.intitule, d)}

    <div>${etiquette("L'ENJEU DU CLIENT", 'enjeu', d)}
      <textarea class="champ" id="b-enjeu" rows="3">${f.enjeu}</textarea>
      <p class="pt sourd" style="margin:4px 0 0">Ce qui ne va pas chez lui et ce que cela lui coûte —
        pas la solution qu'il a en tête.</p></div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      ${champ('CLIENT · NOM INTERNE', 'client', f.client, d)}
      ${champ("SECTEUR D'ACTIVITÉ", 'secteur', f.secteur, d)}
      ${choix('ORDRE DE GRANDEUR', 'taille_client', TAILLES_CLIENT, f.taille_client, d)}
      ${choix('RÉGION', 'region', REGIONS, f.region, d)}
    </div>
    <p class="pt sourd" style="margin:-4px 0 0">Ordre de grandeur et région sont ce qu'une annonce
      publiée dira du client — jamais son nom, jamais une ville.</p>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      <div>${etiquette('RÔLE · GRILLE WAKEA', 'role_grille', d)}
        <select class="champ" id="b-role_grille"><option value="">—</option>
          ${ref.roles.map((r) => h`<option value="${r.cle}" ${r.cle === f.role_grille ? brut('selected') : ''}>${r.libelle}</option>`)}</select></div>
      ${champ('DÉMARRAGE', 'demarrage', f.demarrage, d, 'date')}
      ${champ('DURÉE', 'duree_texte', f.duree_texte, d)}
      ${champ('CHARGE', 'charge_texte', f.charge_texte, d)}
      ${champ('BUDGET DE VENTE · MINIMUM', 'budget_min_eur', f.budget_min_eur, d, 'number')}
      ${champ('BUDGET DE VENTE · MAXIMUM', 'budget_max_eur', f.budget_max_eur, d, 'number')}
    </div>
    ${champ("ZONES D'INTERVENTION · SÉPARÉES PAR DES VIRGULES", 'zones', f.zones.join(', '), d)}

    ${pave('COMPÉTENCES REQUISES · UNE PAR LIGNE', 'competences_requises', f.competences_requises, d, 4,
           'Seulement ce qui est bloquant. Une liste trop longue est la première cause de rapprochement vide.')}
    ${pave('COMPÉTENCES SOUHAITABLES · UNE PAR LIGNE', 'competences_souhaitables', f.competences_souhaitables, d, 3)}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      ${choix('POSTURE IA ATTENDUE', 'intensite_ia', POSTURES_IA, f.intensite_ia, d)}
      ${champ("CE QUE LA MISSION DEMANDE DE FAIRE AVEC L'IA", 'ia_attendu', f.ia_attendu, d)}
    </div>

    ${pave('LIVRABLES · UN PAR LIGNE, « LIVRABLE | ÉCHÉANCE »', 'livrables',
           f.livrables.map((l) => (l.echeance ? `${l.livrable} | ${l.echeance}` : l.livrable)), d, 4,
           "Sans échéance écrite dans la demande, laissez la barre et l'échéance de côté.")}
    ${pave('CRITÈRES DE SUCCÈS · UN PAR LIGNE', 'criteres_succes', f.criteres_succes, d, 3)}
    ${pave('CONTEXTE, CONTRAINTES, EXISTANT', 'contexte', f.contexte, d, 3)}

    <div class="filet-l" style="margin-top:4px"><span class="sur">CE QUI VA DANS LA FICHE DU CANDIDAT</span>
      <span class="pt sourd">ces rubriques ne servent pas au rapprochement</span></div>
    ${champ('RATTACHEMENT HIÉRARCHIQUE OU FONCTIONNEL', 'rattachement', f.rattachement, d)}
    ${pave('EXPÉRIENCE ATTENDUE · UNE PAR LIGNE', 'experience', f.experience, d, 3)}
    ${pave('SAVOIR-ÊTRE · UN PAR LIGNE', 'savoir_etre', f.savoir_etre, d, 3)}
    ${pave('MODALITÉS · UNE PAR LIGNE', 'modalites', f.modalites, d, 3,
           'Lieu, télétravail, statut, dates d’entretien — ce que le candidat demandera de toute façon.')}
    ${pave('PÉRIMÈTRE · UN AXE PAR LIGNE, « AXE | OBJECTIF | INTERLOCUTEURS »', 'axes',
           f.axes.map((a) => [a.axe, a.objectif ?? '', a.interlocuteurs ?? ''].join(' | ')), d, 3)}

    ${f.responsabilites.length ? h`<div>
      <label class="lab">MISSIONS ET RESPONSABILITÉS · REPRISES TELLES QUELLES DANS LA FICHE</label>
      <div class="carte">${f.responsabilites.map((r) => h`<div style="margin-bottom:7px">
        <div class="sur">${r.titre}</div>
        <ul style="margin:3px 0 0;padding-left:17px">${r.points.map((x) =>
          h`<li style="font-size:12px;line-height:1.5">${x}</li>`)}</ul></div>`)}</div>
      <p class="pt sourd" style="margin:4px 0 0">Elles ne s'éditent pas ici : corrigez la demande
        et relancez la lecture si elles sont fausses.</p></div>` : ''}

    ${pave('QUESTIONS À POSER AU CLIENT · UNE PAR LIGNE', 'questions', f.questions, d, 3)}

    <p class="err" id="err" hidden></p>`,
    h`<span class="sm sourd pousse">${cout == null ? 'Saisie manuelle'
        : `Normalisation : ${cout.toFixed(3).replace('.', ',')} €`}</span>
      <button class="bt" data-act="fermer">Abandonner</button>
      <button class="bt p" data-b="creer">Créer la mission</button>`));

  vivante!.empreinte = JSON.stringify(recolter());
}

/** Relit la couche telle qu'elle est à l'écran. */
function recolter() {
  const t = (cle: string) => valeur(`#b-${cle}`) || null;
  const n = (cle: string) => {
    // « 45 000 » se tape aussi naturellement que « 45000 », et l'espace insécable arrive
    // tout seul par copier-coller. Number() en ferait un NaN, que zod refuserait sans
    // dire lequel des trois champs de conditions est en cause.
    const v = valeur(`#b-${cle}`).replace(/[\s\u00a0\u202f]/g, '').replace(/[^\d.-]/g, '');
    if (!v) return null;
    const nombre = Number(v);
    return Number.isFinite(nombre) ? Math.round(nombre) : null;
  };
  const lignes = (cle: string) =>
    (($(`#b-${cle}`) as HTMLTextAreaElement | null)?.value ?? '').split('\n').map((x) => x.trim()).filter(Boolean);

  // « Livrable | Fin du 2e mois » : une barre verticale, parce qu'elle n'apparaît jamais
  // dans un libellé de livrable, là où un tiret ou deux-points y sont courants.
  const morceaux = (ligne: string) => ligne.split('|').map((x) => x.trim());

  return {
    intitule: t('intitule') ?? '',
    client: t('client'), secteur: t('secteur'),
    taille_client: t('taille_client'), region: t('region'), role_grille: t('role_grille'),
    demarrage: t('demarrage'), duree_texte: t('duree_texte'), charge_texte: t('charge_texte'),
    budget_min_eur: n('budget_min_eur'), budget_max_eur: n('budget_max_eur'),
    zones: valeur('#b-zones').split(',').map((z) => z.trim()).filter(Boolean),
    besoin_brut: vivante!.besoin || null,
    brief: {
      // Ce que le modèle a rendu repart entier ; le formulaire ne recouvre que ce qu'il
      // montre. Sans ce point de départ, les responsabilités — affichées mais non
      // éditables — disparaîtraient entre la lecture et l'enregistrement.
      ...vivante!.propose,
      enjeu: t('enjeu') ?? '',
      contexte: t('contexte'),
      rattachement: t('rattachement'),
      livrables: lignes('livrables').map((l) => {
        const [livrable, echeance] = morceaux(l);
        return { livrable: livrable ?? '', echeance: echeance || null };
      }).filter((l) => l.livrable),
      competences_requises: lignes('competences_requises'),
      competences_souhaitables: lignes('competences_souhaitables'),
      intensite_ia: t('intensite_ia'),
      ia_attendu: t('ia_attendu'),
      criteres_succes: lignes('criteres_succes'),
      experience: lignes('experience'),
      savoir_etre: lignes('savoir_etre'),
      modalites: lignes('modalites'),
      axes: lignes('axes').map((l) => {
        const [axe, objectif, interlocuteurs] = morceaux(l);
        return { axe: axe ?? '', objectif: objectif || null, interlocuteurs: interlocuteurs || null };
      }).filter((a) => a.axe),
      questions: lignes('questions'),
    },
  };
}

const dire = (message: string) => { const n = $('#err'); if (n) { n.textContent = message; n.hidden = false; } };

/**
 * Un geste à la fois.
 *
 * Trois clics sur « Créer la mission » ont créé trois missions : rien n'empêchait le
 * deuxième clic de partir pendant que le premier était en vol. Le drapeau les écarte, et
 * le bouton se grise pour que l'écran dise ce qu'il fait au lieu de paraître figé.
 *
 * Ce verrou ne remplace pas la clé d'idempotence côté serveur, il la double : celui-ci
 * protège du doigt, celle-là d'un envoi rejoué, de deux onglets, ou d'un client qui
 * réessaie après un délai dépassé — trois cas où personne n'a cliqué deux fois.
 */
let occupe = false;

function poserEcoute(): void {
  if (ecoute) return;
  ecoute = true;

  $('#couche')?.addEventListener('click', async (ev) => {
    const cible = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-b]');
    const geste = cible?.dataset['b'];
    if (!geste || !vivante || occupe) return;

    if (geste === 'main') {
      vivante.besoin = valeur('#b-besoin');
      vivante.propose = VIDE;
      relecture(VIDE, null);
    }

    if (geste === 'normaliser') {
      vivante.besoin = ($('#b-besoin') as HTMLTextAreaElement | null)?.value.trim() ?? '';
      const choisi = ($('#b-fichier') as HTMLInputElement | null)?.files?.[0] ?? null;
      if (!choisi && vivante.besoin.length < 30)
        return dire('Collez la demande, ou déposez un document.');

      const arreter = attente();
      occupe = true;
      try {
        // Le document l'emporte sur le texte collé : quelqu'un qui dépose une fiche a
        // déjà écrit ce qu'il voulait dire, et c'est elle qui fera foi.
        const r = choisi
          ? await (async () => {
              const forme = new FormData();
              forme.append('fichier', choisi);
              const d = await api.post<{ traitement: string; cout_eur: number; brief: Fiche; besoin: string }>(
                '/api/missions/normaliser-document', forme);
              vivante!.fichier = choisi;
              vivante!.besoin = d.besoin;
              return { traitement: d.traitement, cout_eur: d.cout_eur, fiche: d.brief };
            })()
          : await api.post<{ traitement: string; cout_eur: number; fiche: Fiche }>(
              '/api/missions/normaliser', { besoin: vivante.besoin });
        vivante.traitement = r.traitement;
        vivante.propose = { ...VIDE, ...r.fiche };
        arreter();
        relecture(vivante.propose, r.cout_eur);
      } catch (e) { arreter(); saisie(); dire(direRefus(e)); }
      finally { occupe = false; }
    }

    if (geste === 'creer') {
      const fiche = recolter();
      if (!fiche.intitule) return dire("L'intitulé est obligatoire.");
      if (fiche.brief.enjeu.length < 10) return dire("L'enjeu est obligatoire : c'est lui qui guide la recherche.");
      const suite = JSON.stringify(fiche) === vivante.empreinte ? 'accepte' : 'modifie';
      const bouton = cible as HTMLButtonElement;
      occupe = true;
      bouton.disabled = true;
      const libelle = bouton.textContent;
      bouton.textContent = 'Création…';
      try {
        const { id } = await api.post<{ id: string; reference: string }>('/api/missions',
          { ...fiche, cle_creation: vivante.cle,
            ...(vivante.traitement ? { traitement: vivante.traitement, suite } : {}) });

        // Le document rejoint la mission une fois celle-ci créée, et non avant : il n'y
        // avait pas encore de mission à laquelle l'attacher. S'il échoue, la mission
        // existe quand même et le document se redépose depuis sa fiche — l'inverse
        // laisserait un document orphelin dans le stockage.
        if (vivante.fichier) {
          const forme = new FormData();
          forme.append('fichier', vivante.fichier);
          await api.post(`/api/missions/${id}/document`, forme)
            .catch(() => mot("Mission créée, mais le document n'a pas pu être joint."));
        }

        const fini = vivante.fini;
        vivante = null;
        mot('Mission créée.');
        fini(id);
      } catch (e) {
        // L'échec rend la main : la mission n'existe pas, et la même clé servira au
        // prochain essai — c'est ce qui permet de corriger un champ et de réessayer sans
        // risquer d'en créer deux.
        bouton.disabled = false;
        bouton.textContent = libelle;
        dire(direRefus(e));
      } finally { occupe = false; }
    }
  });
}
